import React, { useEffect, useState, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { Link } from 'react-router-dom';
import {
  Landmark, RefreshCw, Trash2, AlertTriangle, CheckCircle2, Clock,
  CreditCard, Wallet, ChevronDown, ChevronRight, ShieldAlert, Webhook,
  ArrowRight, History,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../components/ui/sonner';

/* Plaid Link, in both of its modes.
 *
 * Without an item, this connects a new institution. With one, it opens update
 * mode against the existing Item so the user re-authenticates the connection
 * they already have — which keeps the access token, the cursor and the imported
 * history, instead of creating a second Item that would re-import everything. */
function PlaidLinkButton({ item, onConnected, onReauthorized, label, className, testId }) {
  const [linkToken, setLinkToken] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.post('/api/accounts/plaid/link-token', item ? { item_id: item.item_id } : {})
      .then(r => { if (!cancelled) setLinkToken(r.data.link_token); })
      .catch(e => {
        if (cancelled) return;
        const detail = e.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : detail?.message || 'Plaid is not configured');
      });
    return () => { cancelled = true; };
  }, [item]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken, metadata) => {
      if (item) onReauthorized(item);
      else onConnected(publicToken, metadata.institution?.name || 'Unknown Bank',
                       metadata.institution?.institution_id || null);
    },
  });

  if (error) {
    return (
      <span className="text-xs text-amber-700" data-testid="link-token-error" title={error}>
        {error}
      </span>
    );
  }

  return (
    <button
      onClick={() => open()}
      disabled={!ready}
      className={className || 'inline-flex items-center gap-2 px-4 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50'}
      data-testid={testId || 'connect-bank-btn'}
    >
      <Landmark size={15} />
      {label || 'Connect Bank Account'}
    </button>
  );
}

/* The environment banner reports what the backend is actually configured to do.
 * It used to be hardcoded to "Sandbox Mode Active", which would have gone on
 * saying sandbox in production. */
function EnvironmentBanner({ status }) {
  if (!status) return null;

  if (!status.configured) {
    return (
      <div className="mb-5 flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3"
        data-testid="plaid-not-configured-banner">
        <ShieldAlert size={16} className="text-slate-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-slate-700">Plaid is not configured</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Set <code className="bg-slate-100 px-1 rounded">PLAID_CLIENT_ID</code> and{' '}
            <code className="bg-slate-100 px-1 rounded">PLAID_SECRET_SANDBOX</code> on the
            backend. Connecting an institution is disabled until then.
          </p>
        </div>
      </div>
    );
  }

  const sandbox = status.plaid_env === 'sandbox';
  return (
    <div className={`mb-5 flex items-start gap-3 rounded-lg px-4 py-3 border ${
      sandbox ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}
      data-testid="plaid-env-banner">
      <AlertTriangle size={16} className={`flex-shrink-0 mt-0.5 ${
        sandbox ? 'text-amber-600' : 'text-emerald-600'}`} />
      <div className="flex-1">
        <p className={`text-xs font-semibold ${sandbox ? 'text-amber-800' : 'text-emerald-800'}`}>
          Plaid {status.plaid_env} &middot; Eyvo {status.eyvo_env}
        </p>
        <p className={`text-xs mt-0.5 ${sandbox ? 'text-amber-700' : 'text-emerald-700'}`}>
          {sandbox ? (
            <>Sandbox test credentials: username{' '}
              <code className="bg-amber-100 px-1 rounded">user_good</code>, password{' '}
              <code className="bg-amber-100 px-1 rounded">pass_good</code>. Any bank shown is
              simulated and no real institution is contacted.</>
          ) : (
            <>Live institutions. Transactions imported here are real.</>
          )}
        </p>
        <p className={`text-xs mt-1 flex items-center gap-1.5 ${
          sandbox ? 'text-amber-700' : 'text-emerald-700'}`}>
          <Webhook size={11} />
          {status.webhook_url_configured
            ? `Webhooks enabled${status.webhook_verification_required ? ' (signature verified)' : ''} — new transactions arrive without a manual sync.`
            : 'No webhook URL configured — transactions arrive on manual or scheduled sync only.'}
        </p>
      </div>
    </div>
  );
}

function StatusChip({ item }) {
  if (item.sync_in_progress) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
        <RefreshCw size={10} className="animate-spin" /> Syncing
      </span>
    );
  }
  if (item.needs_reauth) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-800 text-xs rounded-full font-medium"
        title={item.error_message || 'The bank requires the user to sign in again'}>
        <ShieldAlert size={10} /> Reconnect needed
      </span>
    );
  }
  if (item.status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded-full font-medium"
        title={item.error_message || item.error_code || 'Last sync failed'}>
        <AlertTriangle size={10} /> {item.error_code || 'Error'}
      </span>
    );
  }
  if (!item.is_active) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full font-medium">
        Disconnected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full font-medium">
      <CheckCircle2 size={10} /> Active
    </span>
  );
}

function AccountTypeBadge({ account }) {
  const card = account.is_credit_card;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium ${
      card ? 'bg-violet-50 text-violet-700' : 'bg-sky-50 text-sky-700'}`}
      data-testid={`account-type-${account.plaid_account_id}`}>
      {card ? <CreditCard size={10} /> : <Wallet size={10} />}
      {card ? 'Credit card' : (account.account_subtype || account.account_type || 'Bank')}
    </span>
  );
}

/* Accounts inside one institution. This is the half of the screen that did not
 * exist before: without it a credit card and a chequing account looked the
 * same, and nothing said which GL account a synced transaction lands on. */
function AccountRows({ itemId, accounts, chart, onMap }) {
  const money = (value, currency) => value == null ? '—'
    : `${currency || 'USD'} ${Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (!accounts.length) {
    return (
      <div className="px-5 py-4 text-xs text-slate-500">
        No accounts discovered yet. Run a sync to fetch them from Plaid.
      </div>
    );
  }

  return (
    <table className="w-full text-sm" data-testid={`accounts-${itemId}`}>
      <thead>
        <tr className="border-b border-slate-200 bg-slate-100/60">
          <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Account</th>
          <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
          <th className="text-right px-5 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Balance</th>
          <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Posts to</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {accounts.map(account => (
          <tr key={account.account_id} className="bg-slate-50/60">
            <td className="px-5 py-3">
              <p className="text-sm text-slate-800">{account.name}</p>
              <p className="text-xs text-slate-400">
                {account.official_name || account.account_type}
                {account.mask ? ` ••${account.mask}` : ''}
              </p>
            </td>
            <td className="px-5 py-3"><AccountTypeBadge account={account} /></td>
            <td className="px-5 py-3 text-right text-sm tabular-nums text-slate-700">
              {money(account.current_balance, account.currency)}
              {account.credit_limit != null && (
                <span className="block text-xs text-slate-400">
                  limit {money(account.credit_limit, account.currency)}
                </span>
              )}
            </td>
            <td className="px-5 py-3">
              <select
                value={account.gl_account_id || ''}
                onChange={e => onMap(account, e.target.value)}
                className="text-xs border border-slate-200 rounded px-2 py-1 bg-white max-w-[15rem]"
                data-testid={`gl-map-${account.plaid_account_id}`}
              >
                <option value="">Not mapped</option>
                {chart.map(a => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} — {a.account_name}
                  </option>
                ))}
              </select>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SyncRuns({ runs }) {
  if (!runs.length) return null;
  const fmt = (d) => d ? new Date(d).toLocaleString() : '—';
  return (
    <div className="mt-5 bg-white border border-slate-200 rounded-lg overflow-hidden"
      data-testid="sync-runs">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
        <History size={13} className="text-slate-400" />
        <h3 className="text-xs font-semibold text-slate-700">Recent syncs</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            {['Started', 'Trigger', 'Result', 'Added', 'Modified', 'Removed', 'Skipped', 'Pages'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {runs.map(run => (
            <tr key={run.run_id} className="hover:bg-slate-50">
              <td className="px-4 py-2 text-xs text-slate-600">{fmt(run.started_at)}</td>
              <td className="px-4 py-2 text-xs text-slate-500">{run.trigger}</td>
              <td className="px-4 py-2">
                <span className={`text-xs font-medium ${
                  run.status === 'success' ? 'text-emerald-700'
                    : run.status === 'failed' ? 'text-red-600' : 'text-amber-700'}`}
                  title={run.error_message || ''}>
                  {run.status}{run.error_code ? ` · ${run.error_code}` : ''}
                </span>
              </td>
              <td className="px-4 py-2 text-xs tabular-nums text-slate-700">{run.added}</td>
              <td className="px-4 py-2 text-xs tabular-nums text-slate-700">{run.modified}</td>
              <td className="px-4 py-2 text-xs tabular-nums text-slate-700">{run.removed}</td>
              <td className="px-4 py-2 text-xs tabular-nums text-slate-400"
                title="Re-delivered transactions already held — expected after a resumed sync">
                {run.duplicates_skipped}
              </td>
              <td className="px-4 py-2 text-xs tabular-nums text-slate-400">{run.pages}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BankConnections() {
  const [status, setStatus]   = useState(null);
  const [items, setItems]     = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [chart, setChart]     = useState([]);
  const [runs, setRuns]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, i, a, r] = await Promise.all([
        api.get('/api/accounts/plaid/status'),
        api.get('/api/accounts/plaid/items'),
        api.get('/api/accounts/plaid/accounts'),
        api.get('/api/accounts/plaid/sync-runs?limit=10'),
      ]);
      setStatus(s.data);
      setItems(i.data || []);
      setAccounts(a.data || []);
      setRuns(r.data || []);
    } catch (e) {
      toast.error('Failed to load bank connections');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Assets and liabilities both. A chequing account maps to an asset, but a
    // credit card maps to a liability, so filtering to assets alone would make
    // every card impossible to map.
    api.get('/api/accounts/chart')
      .then(r => setChart((r.data || []).filter(
        a => ['Asset', 'Liability'].includes(a.account_type))))
      .catch(() => setChart([]));
  }, []);

  const errorText = (e, fallback) => {
    const detail = e.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return `${detail.error_code}: ${detail.message}`;
    return fallback;
  };

  const onConnected = async (publicToken, institutionName, institutionId) => {
    try {
      const res = await api.post('/api/accounts/plaid/exchange-token', {
        public_token: publicToken,
        institution_name: institutionName,
        institution_id: institutionId,
        entity: 'Eyvo USA',
      });
      toast.success(`${institutionName} connected — ${res.data.accounts_discovered} account(s) found`);
      load();
    } catch (e) {
      toast.error(errorText(e, 'Failed to connect bank'));
    }
  };

  const onReauthorized = async (item) => {
    toast.success(`${item.institution_name} re-authorised`);
    // Update mode keeps the same Item, so a sync now resumes from the stored
    // cursor rather than re-importing history.
    sync(item.item_id);
  };

  const sync = async (itemId) => {
    setSyncing(s => ({ ...s, [itemId]: true }));
    try {
      const res = await api.post(`/api/accounts/plaid/sync/${itemId}`);
      const { added, modified, removed, duplicate, truncated } = res.data;
      toast.success(
        `Synced: ${added} new, ${modified} updated, ${removed} removed` +
        (duplicate ? `, ${duplicate} already held` : '') +
        (truncated ? ' — more remaining, sync again' : ''));
      load();
    } catch (e) {
      toast.error(errorText(e, 'Sync failed'));
      load();
    }
    setSyncing(s => ({ ...s, [itemId]: false }));
  };

  const syncAll = async () => {
    setSyncing(s => ({ ...s, __all: true }));
    try {
      const res = await api.post('/api/accounts/plaid/sync-all');
      const { synced, failed, imported } = res.data;
      if (failed) toast.error(`${failed} connection(s) failed — see Recent syncs`);
      else toast.success(`${synced} connection(s) synced, ${imported} new transaction(s)`);
      load();
    } catch (e) {
      toast.error(errorText(e, 'Sync failed'));
    }
    setSyncing(s => ({ ...s, __all: false }));
  };

  const disconnect = async (item) => {
    if (!window.confirm(
      `Disconnect ${item.institution_name}? Imported transactions are kept; ` +
      'no further transactions will arrive from this institution.')) return;
    try {
      await api.delete(`/api/accounts/plaid/items/${item.item_id}`);
      toast.success('Disconnected');
      load();
    } catch (e) {
      toast.error(errorText(e, 'Failed to disconnect'));
    }
  };

  const mapAccount = async (account, glAccountId) => {
    try {
      await api.patch(`/api/accounts/plaid/accounts/${account.account_id}`,
                      { gl_account_id: glAccountId });
      setAccounts(list => list.map(a => a.account_id === account.account_id
        ? { ...a, gl_account_id: glAccountId } : a));
      toast.success(glAccountId ? 'Account mapped' : 'Mapping cleared');
    } catch (e) {
      toast.error(errorText(e, 'Failed to map account'));
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString() : 'Never';
  const accountsFor = (itemId) => accounts.filter(a => a.item_id === itemId);
  const canConnect = status?.configured && status?.environment_allowed;

  return (
    <div className="p-6 fade-in" data-testid="bank-connections-page">
      <EnvironmentBanner status={status} />

      {status && !status.environment_allowed && (
        <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
          data-testid="plaid-env-blocked-banner">
          <ShieldAlert size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-red-800">Plaid is blocked in this environment</p>
            <p className="text-xs text-red-700 mt-0.5">{status.environment_note}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Bank Connections
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Plaid-powered bank &amp; credit-card sync for Eyvo USA
            {status?.counts ? ` — ${status.counts.accounts} account(s), ${status.counts.transactions} transaction(s)` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/accounts/usa/plaid-transactions"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-md hover:bg-slate-50 text-slate-700"
            data-testid="view-plaid-transactions">
            Imported transactions <ArrowRight size={13} />
          </Link>
          <button onClick={load} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50"
            title="Reload">
            <RefreshCw size={14} className="text-slate-500" />
          </button>
          {items.length > 0 && (
            <button onClick={syncAll} disabled={!!syncing.__all}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-indigo-200 text-indigo-700 rounded-md hover:bg-indigo-50 disabled:opacity-50"
              data-testid="sync-all-btn">
              <RefreshCw size={13} className={syncing.__all ? 'animate-spin' : ''} />
              {syncing.__all ? 'Syncing…' : 'Sync all'}
            </button>
          )}
          {canConnect && <PlaidLinkButton onConnected={onConnected} onReauthorized={onReauthorized} />}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="plaid-items-list">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading connections…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Landmark size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-600 mb-1">No bank accounts connected</p>
            <p className="text-xs text-slate-400">
              {canConnect
                ? 'Click "Connect Bank Account" above to link your first institution via Plaid.'
                : 'Configure Plaid on the backend to connect an institution.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Institution</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Accounts</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last sync</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(item => {
                const itemAccounts = accountsFor(item.item_id);
                const isOpen = !!expanded[item.item_id];
                return (
                  <React.Fragment key={item.item_id}>
                    <tr className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <button
                          onClick={() => setExpanded(e => ({ ...e, [item.item_id]: !isOpen }))}
                          className="flex items-center gap-2 text-left"
                          data-testid={`expand-${item.item_id}`}
                        >
                          {isOpen ? <ChevronDown size={14} className="text-slate-400" />
                                  : <ChevronRight size={14} className="text-slate-400" />}
                          <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center">
                            <Landmark size={14} className="text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{item.institution_name}</p>
                            <p className="text-xs text-slate-400">{item.entity} · {item.plaid_env}</p>
                          </div>
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-slate-700">{itemAccounts.length}</span>
                          {itemAccounts.some(a => a.is_credit_card) && (
                            <CreditCard size={12} className="text-violet-500" title="Includes a credit card" />
                          )}
                          {itemAccounts.some(a => !a.is_credit_card) && (
                            <Wallet size={12} className="text-sky-500" title="Includes a bank account" />
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4"><StatusChip item={item} /></td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock size={11} />
                          {fmtDate(item.last_sync)}
                        </div>
                        {item.last_sync_status && item.last_sync_status !== 'success' && (
                          <span className="text-xs text-amber-700">{item.last_sync_status}</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {item.needs_reauth && (
                            <PlaidLinkButton
                              item={item}
                              onConnected={onConnected}
                              onReauthorized={onReauthorized}
                              label="Reconnect"
                              testId={`reconnect-btn-${item.item_id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-amber-300 text-amber-800 bg-amber-50 rounded-md hover:bg-amber-100"
                            />
                          )}
                          <button
                            onClick={() => sync(item.item_id)}
                            disabled={!!syncing[item.item_id]}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-indigo-200 text-indigo-700 rounded-md hover:bg-indigo-50 disabled:opacity-50"
                            data-testid={`sync-btn-${item.item_id}`}
                          >
                            <RefreshCw size={11} className={syncing[item.item_id] ? 'animate-spin' : ''} />
                            {syncing[item.item_id] ? 'Syncing…' : 'Sync'}
                          </button>
                          <button
                            onClick={() => disconnect(item)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-red-200 text-red-600 rounded-md hover:bg-red-50"
                            data-testid={`disconnect-btn-${item.item_id}`}
                          >
                            <Trash2 size={11} /> Disconnect
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={5} className="p-0 border-t border-slate-100">
                          {item.error_message && (
                            <div className="px-5 py-2 bg-amber-50 text-xs text-amber-800">
                              {item.error_code}: {item.error_message}
                            </div>
                          )}
                          <AccountRows
                            itemId={item.item_id}
                            accounts={itemAccounts}
                            chart={chart}
                            onMap={mapAccount}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <SyncRuns runs={runs} />

      <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
        <h3 className="text-xs font-semibold text-slate-700 mb-2">How it works</h3>
        <ol className="space-y-1 text-xs text-slate-500 list-decimal list-inside">
          <li>Connect an institution — a secure Plaid modal opens; credentials never reach Eyvo</li>
          <li>Plaid returns the institution's bank and credit-card accounts, with balances</li>
          <li>Map each account to a GL account so synced transactions post to the right place</li>
          <li>Sync imports transactions incrementally; repeating a sync never duplicates them</li>
          <li>Pending transactions are replaced by their settled version, not added alongside it</li>
          <li>A bank payment to a connected Eyvo card is flagged so the spend is not counted twice</li>
        </ol>
      </div>
    </div>
  );
}
